import { observer } from 'mobx-react';
import React, { createRef } from 'react';
import { makeAutoObservable } from 'mobx';
import { IUserConfigPackage } from '../../../API';
import { EntityAPI, EntityType, IDocumentSearchManagedProperty, IDocumentSearchScope } from '../../../API';
import {
	ContextualMenu,
	Icon,
	IContextualMenuItem,
	Pivot,
	PivotItem,
	Stack,
} from '@fluentui/react';

export enum DocumentSearchScopeSelectorStyle {
	Dropdown,
	Tabs,
}

export interface IDocumentSearchScopeSelectorProps {
	state: DocumentSearchScopeSelectorState;
	style: DocumentSearchScopeSelectorStyle;
}

class DocumentSearchScopeSelector extends React.Component<
	IDocumentSearchScopeSelectorProps,
	{}
> {
	private dropdownFieldRef = createRef<HTMLDivElement>();

	componentDidMount(): void {
		this.props.state.ensureLoaded();
	}

	render() {
		if (this.props.state.scopes.length === 0) {
			return <></>;
		}

		if(this.props.style === DocumentSearchScopeSelectorStyle.Tabs){
			return this.tabs();
		} else {
			return this.dropdown();
		}
	}

	tabs() {
		return <Pivot
			selectedKey={this.props.state.selectedScopeId}
			onLinkClick={(p) => {
				this.props.state.setSelectedScopeId(p?.props.itemKey || '');
			}}>
			{this.props.state.scopes.map(scope => <PivotItem itemKey={scope.Id} headerText={scope.Title} />)}
		</Pivot>;
	}

	dropdown() {
		
		let menuItems: IContextualMenuItem[] = [
			{
				key: '',
				text: 'Show all results',
			},
		];

		this.props.state.scopes.forEach((item) => {
			let scope = item as IDocumentSearchScope;
			menuItems.push({
				key: scope.Id,
				text: scope.Title,
			});
		});

		return (
			<div>
				<ContextualMenu
					items={menuItems}
					hidden={!this.props.state.menuVisible}
					target={this.dropdownFieldRef}
					onItemClick={(_, item) => {
						this.props.state.setSelectedScopeId(item?.key || '');
					}}
					onDismiss={() => {
						this.props.state.toggleMenu();
					}}
				/>

				<div
					ref={this.dropdownFieldRef}
					style={{ color: 'rgb(50, 49, 48)', cursor: 'pointer' }}
					onClick={() => {
						this.props.state.toggleMenu();
					}}
				>
					<Stack horizontal>
						<Icon style={{ fontSize: 22 }} iconName="PageList" />
						{this.getSelectedItemText()}
					</Stack>
				</div>
			</div>
		);
	}

	private getSelectedItemText() {
		if (!this.props.state.selectedScopeId) {
			return <></>;
		}

		let scope = this.props.state.scopes.filter(
			(s) => s.Id === this.props.state.selectedScopeId,
		)[0];

		return <span>{scope?.Title}</span>;
	}
}

export class DocumentSearchScopeSelectorState {
	
	constructor(
		userConfig: IUserConfigPackage,
		onChange: (scope?: IDocumentSearchScope) => void,
	) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
		this.onChange = onChange;
	}


	public additionalScopes: IDocumentSearchScope[] = [];
	public addAdditionalScope(scope: IDocumentSearchScope) {
		this.additionalScopes.push(scope);
	}

	async ensureLoaded() {

		let searchScopeApi = await EntityAPI.fromEntityType(EntityType.DocumentSearchScope, this.userConfig) as EntityAPI<IDocumentSearchScope>;
		let setScopesPromise = searchScopeApi.all().then(scopes => {
			this.setScopes(scopes.items);
		});

		let scopePropsApi = await EntityAPI.fromEntityType(EntityType.DocumentSearchManagedProperty, this.userConfig) as EntityAPI<IDocumentSearchManagedProperty>;
		let setManagedPropsPromise =scopePropsApi.all().then(props => {
			this.setManagedProps(props.items);
		});

		await Promise.all([setScopesPromise, setManagedPropsPromise]);
		
		//Show results of the first tab
		this.onChange(this.scopes[0]);
	}

	public managedProps:IDocumentSearchManagedProperty[] = []
	setManagedProps(managedProps: IDocumentSearchManagedProperty[]) {
		this.managedProps = managedProps;
	}

	public scopes:IDocumentSearchScope[] = [];
	setScopes(items: IDocumentSearchScope[]) {
		this.scopes = this.additionalScopes.concat(items);
	}

	public userConfig: IUserConfigPackage;

	public menuVisible: boolean = false;
	public toggleMenu() {
		this.menuVisible = !this.menuVisible;
	}

	private onChange: (scope?: IDocumentSearchScope) => void;

	public selectedScopeId: string = '';
	public setSelectedScopeId(id: string) {
		let scope = this.scopes.find(s => s.Id === id);
		this.onChange(scope);
		this.selectedScopeId = id;
	}
}

export default observer(DocumentSearchScopeSelector);
